// GLOBAL SESSION
import { useMemo, useEffect, useCallback } from 'react';
import axios, { endpoints } from 'src/utils/axios';
import { useSetState } from 'src/hooks/use-set-state';
import { STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { setSession, isValidToken } from './utils';
import { paths } from 'src/routes/paths';
import { useNavigate } from 'react-router';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const { state, setState } = useSetState({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken) {
        setSession(accessToken);

        try {
          const res = await axios.get(endpoints.auth.me);

          if (res.status === 200) {
            const { data } = res.data;

            // Save user data in localStorage
            localStorage.setItem('user', JSON.stringify(data));

            // Update state with user data and accessToken
            setState({ user: { ...data, accessToken }, loading: false });
            if (data?.role?.id === 3 && data?.gym?.status === 'inactive') {
              navigate(paths.dashboard.general.account);
            }
            if (data?.role?.id === 4 && data?.gym?.status === 'inactive') {
              setState({ user: null, loading: false });
              localStorage.removeItem('user');
              localStorage.removeItem('access_token');
            }
            if (data?.role?.id === 5 && data?.gym?.status === 'inactive') {
              setState({ user: null, loading: false });
              localStorage.removeItem('user');
              localStorage.removeItem('access_token');
            }
          } else {
            setState({ user: null, loading: false });
            // Handle non-200 responses (optional)
            console.error('Request failed with status:', res.status);
          }
        } catch (error) {
          setState({ user: null, loading: false });
          // Handle request error (optional)
          console.error('Error fetching user data:', error);
        }
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState, navigate]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            role: state.user?.role ?? 'admin',
          }
        : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
